import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { IGraphApplication } from 'src/app/models/graph/applications/GraphApplication';
import { IGraphAppExposedPermissions } from 'src/app/models/graph/applications/GraphAppExposedPermissions';
import { GraphConstants } from 'src/app/models/graph/Graph.constants';
import { IGenericApiResponse } from 'src/app/models/common/GenericApiResponse';
import { LpDecisionResult } from 'src/app/models/lp-evaluation/LpEvaluationResult';
import { IdampHttpClient } from 'src/app/services/common/IdampHttpClient';

const MICROSOFT_GRAPH_APP_ID = '00000003-0000-0000-c000-000000000000';

// Client-side prompt-injection guard — mirrors 06-guards.ps1.
// Blocks obvious manipulation attempts before the request reaches the backend.
const INJECTION_PATTERNS: RegExp[] = [
    /system\s*:/i,
    /disregard all prior instructions/i,
    /ignore previous instructions/i,
    /\boverride\b/i,
    /maintenance\s+mode/i,
    /\bact\s+as\b/i,
    /auto[-\s]?approv/i,
    /permissionbot/i,
];

interface LpEvaluateRequest {
    permissions: Array<{ name: string; isDelegated: boolean; description?: string }>;
    justification: string;
}

@Injectable({ providedIn: 'root' })
export class LpEvaluatorService {

    constructor(private http: IdampHttpClient) {}

    // ── Public helpers ────────────────────────────────────────────────────────

    isMicrosoftGraph(app: IGraphApplication): boolean {
        if (!app) return false;
        const appId = (app.appId ?? '').toLowerCase();
        const name  = (app.displayName ?? '').toLowerCase();
        return appId === MICROSOFT_GRAPH_APP_ID.toLowerCase()
            || name.includes('microsoft graph')
            || name === 'graph';
    }

    isPromptInjectionRisk(text: string): boolean {
        return INJECTION_PATTERNS.some(p => p.test(text));
    }

    // ── Evaluation entry point ────────────────────────────────────────────────

    async evaluate(
        permissions: IGraphAppExposedPermissions[],
        justification: string,
    ): Promise<LpDecisionResult> {
        if (!environment.lpEvaluator.enabled) {
            throw new Error('AI least-privilege review is not enabled in this environment.');
        }

        // Block obvious injection attempts on the client before the network call
        if (this.isPromptInjectionRisk(justification)) {
            return this.blockedResult();
        }

        const body: LpEvaluateRequest = {
            permissions: permissions.map(p => ({
                name: p.value,
                isDelegated: p.type === GraphConstants.PermissionsScope,
                ...(p.description ? { description: p.description } : {}),
            })),
            justification,
        };

        try {
            const response = await this.http.PostAsync<
                LpEvaluateRequest,
                IGenericApiResponse<LpDecisionResult>
            >('lp-evaluate', body);
            return response.data;
        } catch (err: unknown) {
            // 422 Unprocessable Entity = backend content-safety block
            if ((err as { status?: number })?.status === 422) {
                return this.blockedResult();
            }
            throw err;
        }
    }

    // ── Sentinel results ──────────────────────────────────────────────────────

    blockedResult(): LpDecisionResult {
        return {
            decisionType: 'Clarify',
            recommendedPermissions: [],
            excludedPermissions: [],
            clarificationRequired: true,
            clarificationQuestions: [
                'This justification was flagged by content safety. ' +
                'Please revise it to use clear, technical language before re-evaluating.',
            ],
            explanation: 'Content safety filter was triggered. The request cannot be automatically reviewed.',
        };
    }

    errorResult(message: string): LpDecisionResult {
        return {
            decisionType: 'Clarify',
            recommendedPermissions: [],
            excludedPermissions: [],
            clarificationRequired: false,
            clarificationQuestions: [],
            explanation: message,
        };
    }

}
